import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import InfoBanner, { InfoBannerVariant } from 'app/components/InfoBanner';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface ShowBannerOptions {
  closeable?: boolean;
  variant?: InfoBannerVariant;
  icon?: IconDefinition | null;
  onClose?: () => void;
}

const InfoBannerContext = createContext<{
  content: ReactNode | null;
  showBanner: (content: ReactNode | null, options?: ShowBannerOptions) => void;
  closeBanner: () => void;
  clearBanner: () => void;
}>({
  content: null,
  showBanner: () => null,
  closeBanner: () => null,
  clearBanner: () => null,
});

interface Props {
  children?: ReactNode;
}

const InfoBannerProvider = ({ children }: Props) => {
  const [content, setContent] = useState<ReactNode | null>(null);
  const [closeable, setCloseable] = useState(true);
  const [variant, setVariant] = useState<InfoBannerVariant>('info');
  const [icon, setIcon] = useState<IconDefinition | null | undefined>(
    undefined,
  );
  const onCloseRef = useRef<(() => void) | undefined>(undefined);

  const showBanner = useCallback(
    (content: ReactNode | null, options?: ShowBannerOptions) => {
      setContent(content);
      setCloseable(options?.closeable !== false);
      setVariant(options?.variant ?? 'info');
      setIcon(options?.icon);
      onCloseRef.current = options?.onClose;
    },
    [],
  );

  // A user dismissal: runs onClose, which is where owners persist "don't show again".
  const closeBanner = useCallback(() => {
    setContent(null);
    onCloseRef.current?.();
    onCloseRef.current = undefined;
  }, []);

  // Withdraws a banner that is no longer relevant without treating it as dismissed, so a
  // banner whose condition simply stopped applying is not silenced for the whole session.
  const clearBanner = useCallback(() => {
    setContent(null);
    onCloseRef.current = undefined;
  }, []);

  const value = useMemo(
    () => ({ content, showBanner, closeBanner, clearBanner }),
    [content, showBanner, closeBanner, clearBanner],
  );

  return (
    <InfoBannerContext.Provider value={value}>
      {content && (
        <InfoBanner
          content={content}
          variant={variant}
          icon={icon}
          onClose={closeable ? closeBanner : undefined}
        />
      )}
      {children}
    </InfoBannerContext.Provider>
  );
};

export const useInfoBanner = () => React.useContext(InfoBannerContext);

export default InfoBannerProvider;
